import { useEffect, useRef } from 'react';
import { alertAPI } from '../lib/api';
import { loadSettings, subscribeSettings, playAlarmSound, normalizeCategory } from '../lib/alarmSounds';

/**
 * Poll silencioso de alertas em qualquer tela. Toca o som configurado sempre que
 * um alerta *novo* aparece. Ativado somente enquanto o usuário está autenticado
 * e a opção "Watcher global" estiver ligada nas configurações de sons.
 */
const POLL_MS = 10000;

// Só alertas ainda não reconhecidos interessam ao watcher (um alerta já
// reconhecido nunca deve tocar som) — filtrar no backend encolhe a resposta em
// tenant grande. Em background o tick é pulado; volta a rodar (com tick
// imediato) quando a aba fica visível de novo (achado [5.06]).
const ALARM_LIST_PARAMS = { status: 'active' };

export default function useGlobalAlarmWatcher(enabled) {
  const knownIdsRef = useRef(new Set());
  const primedRef = useRef(false);
  const settingsRef = useRef(loadSettings());

  useEffect(() => {
    const unsub = subscribeSettings((next) => { settingsRef.current = next; });
    return unsub;
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const tick = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      const s = settingsRef.current;
      if (!s.globalEnabled || !s.globalWatcher) return;
      try {
        const res = await alertAPI.list(ALARM_LIST_PARAMS);
        if (cancelled) return;
        const data = res?.data;
        const items = Array.isArray(data) ? data : (data?.alerts || data?.data || []);
        // Primeira passada: apenas registra IDs conhecidos (evita tocar tudo ao entrar no app).
        if (!primedRef.current) {
          items.forEach((a) => knownIdsRef.current.add(a.id || a._id || a.uid || JSON.stringify(a)));
          primedRef.current = true;
          return;
        }
        for (const a of items) {
          const id = a.id || a._id || a.uid || JSON.stringify(a);
          if (knownIdsRef.current.has(id)) continue;
          knownIdsRef.current.add(id);
          const cat = normalizeCategory(a.severity || a.type || a.kind || a.category || a.level);
          playAlarmSound(cat);
        }
      } catch (e) {
        // silencioso — endpoints de alertas podem falhar sem quebrar a UI
      }
    };

    // roda uma vez imediatamente para "primar" o baseline sem tocar sons
    tick();
    const id = setInterval(tick, POLL_MS);
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled]);
}
